import { type ISODate, nonNegativeCurrencySchema, type SuretyAccountId } from "@inrev/common";
import { useUpdateFinancialPeriodBlocSize } from "@portal/api/financials";
import { FormCurrencyComponent } from "@portal/components/form/fieldLayouts";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { createFormResolver } from "@portal/components/multiform";
import { Button, Text } from "@portal/components/primitives";
import { FormSchemaProvider } from "@portal/providers/FormSchemaProvider";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { DateTime } from "luxon";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const blocSizeFormSchema = z.object({
	blocSize: nonNegativeCurrencySchema,
});

type BlocSizeFormValues = z.infer<typeof blocSizeFormSchema>;

export type BlocSizeModalProps = {
	accountId: SuretyAccountId;
	fyeDate: ISODate;
	onClose: () => void;
};

export const BlocSizeModal: FC<BlocSizeModalProps> = ({ accountId, fyeDate, onClose }) => {
	const { mutate: updateBlocSize, isPending: updateBlocSizeIsPending } =
		useUpdateFinancialPeriodBlocSize(accountId);
	const { showErrorFlag } = useFlags();

	const formMethods = useForm<BlocSizeFormValues>({
		resolver: createFormResolver(blocSizeFormSchema),
		defaultValues: {},
		mode: "onBlur",
	});

	const { handleSubmit, formState } = formMethods;

	const onValidSubmit = (data: BlocSizeFormValues) => {
		updateBlocSize(
			{ periodEndDate: fyeDate, blocSize: data.blocSize },
			{
				onSuccess: () => {
					onClose();
				},
				onError: (error) => {
					showErrorFlag({
						heading: "There was an error saving BLOC size",
						message: error.message,
					});
				},
			},
		);
	};

	return (
		<Modal onClickOutside={onClose}>
			<ModalItemWithHeader header="Set BLOC size" onClose={onClose}>
				<FormSchemaProvider value={blocSizeFormSchema}>
					<form
						onSubmit={handleSubmit(onValidSubmit)}
						className="flex flex-col gap-4 px-5 py-4 text-sm text-secondary"
					>
						<Text type="body" color="primary">
							Enter the bank line of credit (BLOC) size for the fiscal year ending{" "}
							<Text type="body-strong" color="primary">
								{DateTime.fromISO(fyeDate).toFormat("MM/dd/yyyy")}
							</Text>
							.
						</Text>
						<FormCurrencyComponent
							formMethods={formMethods}
							name="blocSize"
							label="BLOC size"
							inputClassName="max-w-[300px]"
							testId="bloc-size-input"
						/>
						<div className="flex justify-end gap-2 pt-2">
							<Button
								type="button"
								color="secondary"
								variant="outline"
								onClick={onClose}
								disabled={updateBlocSizeIsPending}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								color="primary"
								loading={updateBlocSizeIsPending}
								disabled={!formState.isDirty || updateBlocSizeIsPending}
							>
								Save
							</Button>
						</div>
					</form>
				</FormSchemaProvider>
			</ModalItemWithHeader>
		</Modal>
	);
};
